const HTMLElementCtor: typeof HTMLElement =
  typeof HTMLElement === 'undefined'
    ? (class {} as unknown as typeof HTMLElement)
    : HTMLElement

/**
 * Shared `<clicky-menu>` for Vue microfrontends, Astro, or plain HTML.
 *
 * **Astro / static HTML:** Omit `data-clicky-defer`. Enhancement runs once after connect
 * (typical parser output already includes the inner `.clicky-menu` list).
 *
 * **Vue + SSR:** Set `data-clicky-defer` so the first HTML and hydration still see `<a>`;
 * after `customElements.whenDefined('clicky-menu')` and `nextTick()`, call
 * `host.enhance()` (see `ClickyMenuNav.vue`).
 *
 * **MFE shell (mobile checkbox + desktop flyouts):** Add `data-mfe-nav-sync` so when the
 * viewport crosses the mobile/desktop band (`data-mfe-nav-mq`, default `(max-width: 976px)`),
 * the host resets flyouts and unchecks the optional shell checkbox (`data-mfe-toggle-id`,
 * default `hda-mfe-nav-toggle`). Same markup works in Vue or Astro with no framework code.
 *
 * **History:** On `popstate` and on `pageshow` when restored from the back-forward cache
 * (`persisted`), the host resets flyouts and the MFE checkbox so the menu does not stay
 * open after browser back/forward between microfrontends.
 */
export class ClickyMenuElement extends HTMLElementCtor {
  static menuCounter = 0

  private menuIndex = 0
  private nextSubmenuIndex = 0
  private menu: HTMLElement | null = null
  private currentMenuItem: HTMLButtonElement | null = null
  private buttonListeners = new Map<HTMLButtonElement, EventListener>()
  /** Delegated click per flyout `ul` for `li[data-clicky-key]` inline submenus */
  private nestedPanelClickListeners = new Map<HTMLElement, EventListener>()
  private menuKeydownListener?: EventListener
  private outsideClickListener?: EventListener
  private customCloseListener?: EventListener
  private closeDispatcherListener?: EventListener
  private globalListenersAttached = false
  private syncScheduled = false
  /** Optional: `data-mfe-nav-sync` — reset flyout + mobile checkbox when viewport crosses mq */
  private mfeNavBreakpointMql?: MediaQueryList
  private mfeNavBreakpointListener?: () => void
  /** Back/forward and bfcache restore — keep shell checkbox and flyouts in sync */
  private historyPopstateListener?: EventListener
  private historyPageshowListener?: EventListener

  /**
   * Run link→button enhancement and attach listeners. Safe to call again (idempotent).
   * Clears `data-clicky-defer` if present, then syncs.
   */
  enhance() {
    this.removeAttribute('data-clicky-defer')
    this.scheduleSync()
  }

  connectedCallback() {
    this.scheduleSync()
  }

  disconnectedCallback() {
    this.detachMfeNavBreakpointSync()
    this.syncScheduled = false
    this.unbindAllListeners()
    this.globalListenersAttached = false
    this.currentMenuItem = null
    this.menu = null
    this.menuIndex = 0
    this.nextSubmenuIndex = 0
  }

  /** Coalesce multiple `connectedCallback` / `enhance()` triggers into one `sync` on the next microtask. */
  private scheduleSync() {
    if (this.syncScheduled) return
    this.syncScheduled = true
    queueMicrotask(() => {
      this.syncScheduled = false
      this.sync()
    })
  }

  private sync() {
    if (this.menu && !this.menu.isConnected) {
      this.unbindAllListeners()
      this.globalListenersAttached = false
      this.currentMenuItem = null
      this.menu = null
      this.menuIndex = 0
      this.nextSubmenuIndex = 0
    }

    if (!this.menu) {
      const found = this.querySelector('.clicky-menu')
      if (!found) return
      this.menu = found as HTMLElement
      this.menuIndex = ++ClickyMenuElement.menuCounter
      this.nextSubmenuIndex = 0
    }

    if (this.hasAttribute('data-clicky-defer')) return

    if (!this.globalListenersAttached) {
      this.attachGlobalListeners()
      this.globalListenersAttached = true
    }

    this.menuSetup()
    this.attachMfeNavBreakpointSync()
  }

  /**
   * When the MFE layout uses a mobile checkbox outside this host, flyout open state and
   * `:checked` can get out of sync across resize. One listener, no Vue/Astro code.
   */
  private attachMfeNavBreakpointSync() {
    if (!this.hasAttribute('data-mfe-nav-sync')) return
    if (typeof window === 'undefined' || this.mfeNavBreakpointMql) return

    const mq = this.getAttribute('data-mfe-nav-mq') ?? '(max-width: 976px)'

    this.mfeNavBreakpointMql = window.matchMedia(mq)
    this.mfeNavBreakpointListener = () => {
      this.resetMfeNavShellState()
    }
    this.mfeNavBreakpointMql.addEventListener(
      'change',
      this.mfeNavBreakpointListener
    )
  }

  private detachMfeNavBreakpointSync() {
    if (this.mfeNavBreakpointMql && this.mfeNavBreakpointListener) {
      this.mfeNavBreakpointMql.removeEventListener(
        'change',
        this.mfeNavBreakpointListener
      )
    }
    this.mfeNavBreakpointMql = undefined
    this.mfeNavBreakpointListener = undefined
  }

  /** Close flyouts; when `data-mfe-nav-sync`, uncheck the shell mobile nav checkbox. */
  private resetMfeNavShellState() {
    this.close()
    if (!this.hasAttribute('data-mfe-nav-sync')) return
    const toggleId =
      this.getAttribute('data-mfe-toggle-id') ?? 'hda-mfe-nav-toggle'
    const el = document.getElementById(toggleId)
    if (el instanceof HTMLInputElement) el.checked = false
  }

  private attachGlobalListeners() {
    if (!this.menu) return

    this.outsideClickListener = e =>
      this.closeIfClickOutsideMenu(e as MouseEvent)
    this.customCloseListener = () => this.closeOpenSubmenu()
    this.closeDispatcherListener = e => this.dispatchMenuClose(e as MouseEvent)
    this.menuKeydownListener = e => this.onMenuKeydown(e as KeyboardEvent)

    document.addEventListener('click', this.outsideClickListener)
    this.menu.addEventListener('clickyMenusClose', this.customCloseListener)
    this.menu.addEventListener('keydown', this.menuKeydownListener)
    document.addEventListener('click', this.closeDispatcherListener)

    if (typeof window !== 'undefined') {
      this.historyPopstateListener = () => this.resetMfeNavShellState()
      window.addEventListener('popstate', this.historyPopstateListener)
      this.historyPageshowListener = e => {
        const pe = e as PageTransitionEvent
        if (pe.persisted) this.resetMfeNavShellState()
      }
      window.addEventListener('pageshow', this.historyPageshowListener)
    }
  }

  close() {
    this.closeOpenSubmenu()
  }

  private menuSetup() {
    if (!this.menu) return

    this.menu.classList.remove('no-js')
    const submenuSelector = this.menu.dataset.clickySubmenuSelector || 'ul'

    this.menu
      .querySelectorAll<HTMLElement>(submenuSelector)
      .forEach(submenu => {
        const menuItem = submenu.parentElement
        if (!menuItem) return

        const first = menuItem.firstElementChild
        if (first instanceof HTMLButtonElement) return

        const button = this.convertLinkToButton(menuItem)
        this.setUpAria(submenu, button, this.nextSubmenuIndex)
        this.nextSubmenuIndex += 1

        const onButtonClick: EventListener = e => {
          this.toggleOnMenuClick(e as MouseEvent)
        }

        button.addEventListener('click', onButtonClick)
        this.buttonListeners.set(button, onButtonClick)

        this.nestedInlineSetup(submenu)
      })
  }

  /**
   * Inline rows: `li[data-clicky-key]` toggles visibility of `li[data-clicky-parent-key]`
   * (mutually exclusive per panel `ul`). Visibility uses `hidden`; active section is reflected
   * with `aria-expanded` on each key row’s control (no separate panel attribute). No-JS: all rows stay visible.
   */
  private nestedInlineSetup(panel: HTMLElement) {
    if (!panel.querySelector('li[data-clicky-key]')) return
    if (this.nestedPanelClickListeners.has(panel)) return

    /* Key rows are disclosure controls: `<a>` without `href` is not tab-focusable; use `<button>`. */
    this.convertNestedKeyRowsToButtons(panel)

    this.initNestedPanelState(panel)

    const listener: EventListener = e =>
      this.onNestedPanelClick(e as MouseEvent, panel)
    panel.addEventListener('click', listener)
    this.nestedPanelClickListeners.set(panel, listener)
  }

  private initNestedPanelState(panel: HTMLElement) {
    this.resetNestedPanel(panel)
  }

  private resetNestedPanel(panel: HTMLElement) {
    panel
      .querySelectorAll<HTMLElement>('li[data-clicky-parent-key]')
      .forEach(li => {
        li.hidden = true
      })
    this.syncNestedKeyAria(panel, null)
  }

  /** Which nested section is open, derived from `aria-expanded` on key rows (at most one). */
  private getExpandedNestedKey(panel: HTMLElement): string | null {
    for (const li of panel.querySelectorAll<HTMLElement>(
      'li[data-clicky-key]'
    )) {
      const el = li.querySelector<HTMLElement>('a, button')
      if (el?.getAttribute('aria-expanded') === 'true')
        return li.getAttribute('data-clicky-key')
    }
    return null
  }

  private expandNestedPanel(panel: HTMLElement, key: string) {
    panel
      .querySelectorAll<HTMLElement>('li[data-clicky-parent-key]')
      .forEach(li => {
        const pk = li.getAttribute('data-clicky-parent-key')
        li.hidden = pk !== key
      })
    this.syncNestedKeyAria(panel, key)
  }

  private syncNestedKeyAria(panel: HTMLElement, expandedKey: string | null) {
    panel.querySelectorAll<HTMLElement>('li[data-clicky-key]').forEach(li => {
      const k = li.getAttribute('data-clicky-key')
      const el = li.querySelector<HTMLElement>('a, button')
      if (!el || !k) return
      el.setAttribute(
        'aria-expanded',
        expandedKey !== null && expandedKey === k ? 'true' : 'false'
      )
    })
  }

  private onNestedPanelClick(e: MouseEvent, panel: HTMLElement) {
    const t = e.target
    if (!(t instanceof HTMLElement)) return

    const keyLi = t.closest('li[data-clicky-key]')
    if (!keyLi || !panel.contains(keyLi)) return

    const key = keyLi.getAttribute('data-clicky-key')
    if (!key) return

    e.preventDefault()
    e.stopPropagation()

    const current = this.getExpandedNestedKey(panel)
    if (current === key) this.resetNestedPanel(panel)
    else this.expandNestedPanel(panel, key)
  }

  private clearNestedPanelListeners() {
    this.nestedPanelClickListeners.forEach((listener, panel) => {
      panel.removeEventListener('click', listener)
    })
    this.nestedPanelClickListeners.clear()
  }

  /**
   * Disclosure-style control: same visible content, `type="button"`, copy attrs except `href`.
   */
  private anchorToButton(anchor: HTMLAnchorElement): HTMLButtonElement {
    const button = document.createElement('button')
    button.type = 'button'
    button.innerHTML = anchor.innerHTML.trim()
    const linkAtts = anchor.attributes
    for (let i = 0; i < linkAtts.length; i += 1) {
      const attr = linkAtts[i]
      if (!attr) continue
      if (attr.name === 'href') continue
      button.setAttribute(attr.name, attr.value)
    }
    return button
  }

  /** Each `li[data-clicky-key]` uses a direct child `<a>` in markup; replace with `<button>`. */
  private convertNestedKeyRowsToButtons(panel: HTMLElement) {
    panel.querySelectorAll<HTMLElement>('li[data-clicky-key]').forEach(li => {
      const link = li.querySelector(':scope > a')
      if (!(link instanceof HTMLAnchorElement)) return
      li.replaceChild(this.anchorToButton(link), link)
    })
  }

  private convertLinkToButton(menuItem: Element): HTMLButtonElement {
    const link = menuItem.getElementsByTagName('a')[0]
    if (!link) return document.createElement('button')
    const button = this.anchorToButton(link)
    menuItem.replaceChild(button, link)
    return button
  }

  private setUpAria(
    submenu: HTMLElement,
    button: HTMLButtonElement,
    index: number
  ) {
    const submenuId = submenu.getAttribute('id')
    const idSuffix = `-submenu-${this.menuIndex}-${index}`
    const id =
      submenuId === null
        ? (button.textContent || '')
            .trim()
            .replace(/\s+/g, '-')
            .replace(/^[^a-zA-Z]+|[^\w:.-]+/g, '')
            .toLowerCase() + idSuffix
        : submenuId + idSuffix

    button.setAttribute('aria-controls', id)
    button.setAttribute('aria-expanded', 'false')
    submenu.setAttribute('id', id)
    submenu.setAttribute('aria-hidden', 'true')
  }

  private toggleOnMenuClick(e: MouseEvent) {
    const button = e.currentTarget as HTMLButtonElement
    if (this.currentMenuItem && button !== this.currentMenuItem) {
      this.toggleSubmenu(this.currentMenuItem)
    }
    this.toggleSubmenu(button)
  }

  private toggleSubmenu(button: HTMLButtonElement) {
    const submenu = this.getSubmenuForButton(button)
    if (!submenu) return

    if ('true' === button.getAttribute('aria-expanded')) {
      button.setAttribute('aria-expanded', 'false')
      submenu.setAttribute('aria-hidden', 'true')
      this.resetNestedPanel(submenu)
      this.currentMenuItem = null
    } else {
      button.setAttribute('aria-expanded', 'true')
      submenu.setAttribute('aria-hidden', 'false')
      this.preventOffscreenSubmenu(submenu)
      this.currentMenuItem = button
    }
  }

  private closeOpenSubmenu() {
    if (this.currentMenuItem) this.toggleSubmenu(this.currentMenuItem)
  }

  /** Direct row controls inside a flyout; skips `li[hidden]` (nested subtree rows). */
  private getPanelFocusables(panel: HTMLElement): HTMLElement[] {
    return Array.from(
      panel.querySelectorAll<HTMLElement>(
        'li:not([hidden]) > a, li:not([hidden]) > button'
      )
    ).filter(el => !(el instanceof HTMLButtonElement && el.disabled))
  }

  private onMenuKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeOnEscKey(event)
      return
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

    const menu = this.menu
    if (!menu) return

    const target = event.target
    if (!(target instanceof HTMLElement) || !menu.contains(target)) return

    const openPanel = menu.querySelector<HTMLElement>('ul[aria-hidden="false"]')
    if (!openPanel?.contains(target)) return

    this.moveFocusInOpenPanel(
      openPanel,
      event.key === 'ArrowDown' ? 1 : -1,
      target
    )
    event.preventDefault()
  }

  /**
   * ArrowDown/Up between visible panel rows; Up on first row moves focus to the top-level opener.
   */
  private moveFocusInOpenPanel(
    panel: HTMLElement,
    delta: 1 | -1,
    from: HTMLElement
  ) {
    const items = this.getPanelFocusables(panel)
    if (items.length === 0) return

    const control = from.closest('a, button')
    let i = control instanceof HTMLElement ? items.indexOf(control) : -1
    if (i === -1) i = delta > 0 ? 0 : items.length - 1

    if (delta < 0 && i === 0 && this.currentMenuItem) {
      this.currentMenuItem.focus()
      return
    }

    let next = i + delta
    if (next >= items.length) next = 0
    if (next < 0) next = items.length - 1
    const toFocus = items[next]
    if (toFocus) toFocus.focus()
  }

  private closeOnEscKey(event: KeyboardEvent) {
    if (!this.currentMenuItem) return
    if (event.key !== 'Escape') return

    const target = event.target as HTMLElement | null
    if (!target) return

    if (target.closest('ul[aria-hidden="false"]') !== null) {
      this.currentMenuItem.focus()
      this.toggleSubmenu(this.currentMenuItem)
      event.preventDefault()
    } else if ('true' === target.getAttribute('aria-expanded')) {
      this.toggleSubmenu(this.currentMenuItem)
      event.preventDefault()
    }
  }

  private closeIfClickOutsideMenu(event: MouseEvent) {
    if (!this.currentMenuItem) return
    const target = event.target as Node | null
    if (target && !this.contains(target))
      this.toggleSubmenu(this.currentMenuItem)
  }

  private dispatchMenuClose(event: MouseEvent) {
    const target = event.target as HTMLElement | null
    if (!target || !this.menu || !this.menu.id) return

    const closer = target.closest<HTMLElement>('[data-clicky-menus-close]')
    if (!closer) return

    const menuId = closer.getAttribute('data-clicky-menus-close')
    if (menuId === this.menu.id)
      this.menu.dispatchEvent(new Event('clickyMenusClose'))
  }

  private getSubmenuForButton(button: HTMLButtonElement): HTMLElement | null {
    const controls = button.getAttribute('aria-controls')
    if (!controls) return null
    return document.getElementById(controls)
  }

  private preventOffscreenSubmenu(submenu: HTMLElement) {
    submenu.classList.remove('sub-menu--right')

    /* Top-level dropdowns: wide/centered layout is controlled in CSS (see clicky-menu.css). */
    if (submenu.parentElement?.parentElement === this.menu) return

    const screenWidth =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth
    const parent = submenu.offsetParent as HTMLElement | null
    if (!parent) return

    const menuLeftEdge = parent.getBoundingClientRect().left
    const menuRightEdge = menuLeftEdge + submenu.offsetWidth
    if (menuRightEdge + 32 > screenWidth)
      submenu.classList.add('sub-menu--right')
  }

  private unbindAllListeners() {
    this.buttonListeners.forEach((listener, button) => {
      button.removeEventListener('click', listener)
    })
    this.buttonListeners.clear()
    this.clearNestedPanelListeners()

    if (this.menu && this.menuKeydownListener) {
      this.menu.removeEventListener('keydown', this.menuKeydownListener)
    }
    if (this.menu && this.customCloseListener) {
      this.menu.removeEventListener(
        'clickyMenusClose',
        this.customCloseListener
      )
    }
    if (this.outsideClickListener) {
      document.removeEventListener('click', this.outsideClickListener)
    }
    if (this.closeDispatcherListener) {
      document.removeEventListener('click', this.closeDispatcherListener)
    }
    if (typeof window !== 'undefined') {
      if (this.historyPopstateListener) {
        window.removeEventListener('popstate', this.historyPopstateListener)
      }
      if (this.historyPageshowListener) {
        window.removeEventListener('pageshow', this.historyPageshowListener)
      }
    }
    this.historyPopstateListener = undefined
    this.historyPageshowListener = undefined

    this.menuKeydownListener = undefined
    this.customCloseListener = undefined
    this.outsideClickListener = undefined
    this.closeDispatcherListener = undefined
  }
}

if (
  typeof window !== 'undefined' &&
  typeof customElements !== 'undefined' &&
  !customElements.get('clicky-menu')
) {
  customElements.define('clicky-menu', ClickyMenuElement)
}
